import DatePicker from "react-datepicker";
import {
  Box,
  Flex,
  TableContainer,
  Table,
  Tbody,
  Tr,
  Td,
  Button,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Text,
  Input,
  HStack,
  Select as BasicSelect,
  Thead,
  Th,
  FormControl,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { Icon } from "@chakra-ui/react";
import accounting from "accounting";
import MoneyField from "components/elements/MoneyInput";
import PercentField from "components/elements/PercentField";
import moment from "moment";
import { useEffect, useState } from "react";
import { stringToFullNumber } from "utils/helpers";
import SaveScenario from "./SaveScenario";
import { PMT } from "./calculations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScenarioMonth from "./ScenarioMonth";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext } from "react";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useCustomFormats } from "hooks/useCustomFormat";

const ScenarioHeaderCell = ({ label, value }: { label: string; value: string }) => {
  const { user } = useTenant();
  const custom_currency =
    user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";

  if (label === "Loan Amount") {
    value = accounting.formatMoney(value).replace("$", custom_currency);
  }
  return (
    <Box textAlign="left">
      <Text fontSize="14px" fontWeight="500" mb="2px">
        {label}
      </Text>
      <Text fontSize="16px">{value}</Text>
    </Box>
  );
};

export const calculateScenario = (scenario: any) => {
  // Calculate Interest Rate
  const interest_rate = scenario.benchmark / 10000 + scenario.spread_bps / 10000;

  const monthlyInterestRate = interest_rate / 12;
  // const amortizingMonths = (scenario.loan_term - scenario.interest_only) * 12;
  const amortizingMonths = scenario.amortization_months * 12;
  const interest_only_payment = scenario.amount * monthlyInterestRate;

  // calculate amortizing payment
  const amortizing_payment = PMT(interest_rate / 12, amortizingMonths, scenario.amount * -1, 0, 0);

  return {
    interest_rate,
    interest_only_payment,
    amortizing_payment,
  };
};

const Scenario = ({
  initScenario,
  index,
  copyScenario,
}: {
  initScenario: any;
  index: number;
  copyScenario: (template: any) => void;
}) => {
  const [scenario, setScenario] = useState(initScenario);
  const [results, setResults] = useState(initScenario.results || {});
  const [viewSchedule, setViewSchedule] = useState(false);
  const [months, setMonths] = useState<any[]>([]);

  const { user } = useTenant();

  const { datePickerFormat } = useCustomFormats();

  const updateScenario = (field: string, val: string) => {
    let value: string | number = val;
    if (
      field === "amount" ||
      field === "loan_term" ||
      field === "interest_only" ||
      field === "amortization_months"
    ) {
      value = stringToFullNumber(val);
    }

    setScenario({
      ...scenario,
      [field]: value,
    });
  };

  useEffect(() => {
    setResults(calculateScenario(scenario));
  }, [scenario]);

  useEffect(() => {
    const month_count = scenario.loan_term * 12;
    const month_array = Array.from(Array(month_count).keys());

    let TOTAL_WEIGHT = 0;
    let TOTAL_PRINCIPAL = 0;

    let last_ending_balance = 0;

    const months_data = month_array.map(index => {
      const annual_rate = scenario.interest_rate_type === "Fixed" ? results.interest_rate : "TBD";

      const interest_only = index <= scenario.interest_only * 12 ? "Yes" : "No";

      const beginning_balance = interest_only === "Yes" ? scenario.amount : last_ending_balance;

      const interest =
        (scenario.interest_calculation === "Actual 365"
          ? annual_rate * (30 / 365)
          : annual_rate * (30 / 360)) * beginning_balance;

      let payment = 0;
      /* Calculate Payment */
      if (scenario.interest_rate_type === "Fixed") {
        if (interest_only === "Yes") {
          payment = interest;
        } else {
          payment = results.amortizing_payment;
        }
      } else {
        // PMT

        const amortizingMonths = scenario.amortization_months * 12;

        payment = PMT(
          annual_rate / 12,
          amortizingMonths - index + 1,
          beginning_balance * -1, // beg balance
          0,
          0
        );
      }

      // TODO: calculate principal
      const principal = payment - interest;
      const ending_balance = beginning_balance - principal;

      last_ending_balance = ending_balance;
      const weight = index * interest;
      TOTAL_WEIGHT += weight;
      TOTAL_PRINCIPAL += principal;

      return {
        month: index,
        Days: scenario.interest_calculation === "30/360" ? 30 : "TBD",
        "EO Month": moment(scenario.funding_date).add(index, "month").format("MM/DD/YY"),
        "Interest Only": interest_only,
        "Ann. Rate": annual_rate,
        "Beginning Balance": beginning_balance,
        Payment: payment,
        Principal: principal,
        Interest: interest,
        "Ending Balance": ending_balance,
        "Lender CF": principal + interest,
        Weight: weight,
      };
    });
    months_data.forEach((m: any) => {
      TOTAL_WEIGHT += m.Weight;
      TOTAL_PRINCIPAL += m.Principal;
    });

    const WAL = TOTAL_WEIGHT / TOTAL_PRINCIPAL / 12;

    setMonths(months_data);
  }, [results]);

  return (
    <AccordionItem
      mb="22px"
      borderWidth="0px"
      _last={{
        borderBottomWidth: "0px",
      }}
    >
      <Box borderColor="gray.100" borderWidth="1px" borderRadius="5px">
        <h2>
          <AccordionButton
            py="12px"
            px="24px"
            display="block"
            _hover={{
              borderRadius: "5px",
              bg: "rgba(0, 0, 0, 0.04)",
            }}
          >
            <Flex as="span" alignItems="center" flex="1" justifyContent="space-between">
              <Text
                as="span"
                display="block"
                flex="1"
                textAlign="left"
                color="black"
                fontSize="16px"
                fontWeight="600"
              >
                Scenario #{index + 1}
              </Text>
              <AccordionIcon />
            </Flex>
            <HStack mt="8px" justifyContent="space-between" maxW="680px">
              <ScenarioHeaderCell
                label="Loan Amount"
                value={accounting.formatMoney(scenario.amount)}
              />
              <ScenarioHeaderCell
                label="Interest Rate"
                value={`${scenario.interest_rate_type} - ${(results.interest_rate * 100).toFixed(
                  2
                )}%`}
              />
              <ScenarioHeaderCell
                label="Benchmark"
                value={`${(scenario.benchmark / 100).toFixed(2)}%`}
              />
              <ScenarioHeaderCell label="Loan Term" value={`${scenario.loan_term} years`} />
              <ScenarioHeaderCell
                label="Amortization"
                value={`${scenario.amortization_months} years`}
              />
            </HStack>
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <TableContainer mb="20px">
            <Table size="sm" variant="scenario-settings">
              <Thead bg="#EDEDED" borderRadius="5px">
                <Tr px="10px">
                  <Th py="10px" fontSize="14px" fontWeight="500" textTransform="capitalize">
                    Values
                  </Th>
                  <Th
                    py="10px"
                    fontSize="14px"
                    fontWeight="500"
                    textAlign="right"
                    textTransform="capitalize"
                  >
                    Context
                  </Th>
                  <Th
                    py="10px"
                    fontSize="14px"
                    fontWeight="500"
                    textAlign="right"
                    pr="18px"
                    textTransform="capitalize"
                  >
                    Yearly
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Proposed Loan Amount</Td>
                  <Td>
                    <MoneyField variant="scenario" value="-" disabled onChange={() => {}} />
                  </Td>
                  <Td>
                    <MoneyField
                      variant="scenario"
                      value={scenario.amount}
                      onValueChange={val => updateScenario("amount", val ?? "")}
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Loan Term</Td>
                  <Td>
                    <Input
                      isDisabled
                      value={`${scenario.loan_term * 12} months`}
                      variant="scenario"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      flex="1"
                      bg="#F9F8F8"
                      borderColor="#F9F8F8"
                      value={scenario.loan_term}
                      onChange={ev => updateScenario("loan_term", ev.target.value)}
                      placeholder="0"
                      variant="scenario"
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Interest-Only</Td>
                  <Td>
                    <Input
                      value={`${scenario.interest_only * 12} months`}
                      disabled
                      variant="scenario"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      value={scenario.interest_only}
                      onChange={ev => updateScenario("interest_only", ev.target.value)}
                      placeholder="0"
                      variant="scenario"
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Amortization</Td>
                  <Td>
                    <Input
                      value={`${scenario.amortization_months * 12} months`}
                      disabled
                      variant="scenario"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      value={scenario.amortization_months}
                      onChange={ev => updateScenario("amortization_months", ev.target.value)}
                      placeholder="0"
                      variant="scenario"
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Loan Closing Date</Td>
                  <Td>
                    <Input value="-" disabled variant="scenario" />
                  </Td>
                  <Td width={"2%"}>
                    {/* In above Td if add background='' then the default background color is added here */}
                    {/* <Input
                      value={scenario.funding_date}
                      onChange={(ev) =>
                        updateScenario("funding_date", ev.target.value)
                      }
                      placeholder="MM/DD/YYYY"
                      type="date"
                      variant="scenario"
                    /> */}

                    <FormControl display="flex" justifyContent="space-between">
                      <Box width="100%" border={"2px solid #E2E8F0"} p={"8px"} borderRadius={"5px"}>
                        <DatePicker
                          selected={scenario.funding_date ? new Date(scenario.funding_date) : null}
                          onChange={date =>
                            updateScenario("funding_date", date ? date.toISOString() : "")
                          }
                          dateFormat={datePickerFormat}
                          placeholderText={datePickerFormat}
                          wrapperClassName="date-picker-wrapper input"
                        />
                        <button
                          onClick={() => {
                            const datePickerInput = document.querySelector(
                              ".date-picker-wrapper input"
                            ) as HTMLInputElement | null;
                            // Triggering the DatePicker input when clicked
                            datePickerInput?.click();
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            position: "absolute",
                            top: "50%",
                            right: "10px",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <Icon as={CalendarIcon} color="#0000007A" />
                        </button>
                      </Box>
                    </FormControl>
                  </Td>
                </Tr>
                <Tr>
                  <Td>Interest Rate Type</Td>
                  <Td>
                    <Input value="-" disabled variant="scenario" />
                  </Td>
                  <Td>
                    <BasicSelect
                      variant="scenario"
                      placeholder="Choose Type"
                      height="36px"
                      value={scenario.interest_rate_type}
                      onChange={ev => updateScenario("interest_rate_type", ev.target.value)}
                    >
                      <option value="Fixed">Fixed</option>
                      <option value="Floating">Floating</option>
                    </BasicSelect>
                  </Td>
                </Tr>
                <Tr>
                  <Td>Spread</Td>
                  <Td>
                    <Input value="(bps)" disabled variant="scenario" />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      value={scenario.spread_bps}
                      onChange={ev => updateScenario("spread_bps", ev.target.value)}
                      placeholder="0"
                      variant="scenario"
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Interest Rate Calculation</Td>
                  <Td>
                    <Input value="-" disabled variant="scenario" />
                  </Td>
                  <Td>
                    <BasicSelect
                      variant="scenario"
                      placeholder="Choose Type"
                      height="36px"
                      value={scenario.interest_calculation}
                      onChange={ev => updateScenario("interest_calculation", ev.target.value)}
                    >
                      <option value="30/360">30/360</option>
                      <option value="Actual/360">Actual/360</option>
                      <option value="Actual/365">Actual/365</option>
                    </BasicSelect>
                  </Td>
                </Tr>
                <Tr>
                  <Td>Benchmark</Td>
                  <Td>
                    <Input value="(10 year UST)" disabled variant="scenario" />
                  </Td>
                  <Td>
                    <PercentField
                      value={(scenario.benchmark / 100).toFixed(2)}
                      onChange={val => updateScenario("benchmark", val)}
                      variant="scenario"
                    />
                  </Td>
                </Tr>

                <Tr>
                  <Td colSpan={3} fontWeight="600" pt="20px">
                    Loan Payment (1 Month)
                  </Td>
                </Tr>
                <Tr>
                  <Td>Interest Rate</Td>
                  <Td></Td>
                  <Td>
                    <PercentField
                      value={(results.interest_rate * 100).toFixed(2)}
                      onChange={val => {}}
                      disabled
                      variant="scenario"
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Amortizing Payment</Td>
                  <Td></Td>
                  <Td color="red">
                    <MoneyField
                      variant="scenario"
                      value={results.amortizing_payment}
                      disabled
                      onChange={() => {}}
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td>Interest-Only Payment</Td>
                  <Td></Td>
                  <Td>
                    <MoneyField
                      variant="scenario"
                      value={results.interest_only_payment}
                      disabled
                      onChange={() => {}}
                    />
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
          <Flex bg="#F9F8F8" p="12px" borderRadius="12px" justifyContent="space-between">
            <Box>
              <Button
                onClick={() => setViewSchedule(!viewSchedule)}
                size="sm"
                variant={getCustomButtonVariant(user)}
                mr="10px"
                leftIcon={<FontAwesomeIcon icon={["far", "calendar"]} />}
              >
                {viewSchedule ? "Hide" : "Show"} Schedule
              </Button>
              <Button
                size="sm"
                variant={getCustomButtonVariant(user)}
                leftIcon={<FontAwesomeIcon icon={["far", "pie-chart"]} />}
              >
                Analytics
              </Button>
            </Box>
            <SaveScenario scenario={scenario} />
          </Flex>
          {viewSchedule && (
            <Flex overflowX="scroll" maxW="600px" mt="20px">
              {months.map((month, index) => (
                <ScenarioMonth month={month} index={index} />
              ))}
            </Flex>
          )}
        </AccordionPanel>
      </Box>
      <Flex justifyContent="flex-end" borderWidth="0px" borderColor="white">
        <Button size="sm" w="auto" variant="ghost" onClick={() => copyScenario(scenario)}>
          Duplicate
        </Button>
      </Flex>
    </AccordionItem>
  );
};

export default Scenario;
